/**
 * Routes and providers.
 *
 * Admin pages are lazily loaded: a visitor searching for a cement supplier
 * should not download the crawl monitor. Public pages are in the main bundle
 * because they are the first thing anyone sees, and a chunk request on the
 * critical path is worse than the bytes it saves.
 */

import { lazy, Suspense } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { AdminLayout } from '@/layouts/AdminLayout'
import { PublicLayout } from '@/layouts/PublicLayout'
import { Spinner } from '@/components/ui/primitives'
import { AuthProvider } from '@/stores/auth'
import { ApiError } from '@/services/client'

import { AccountPage } from '@/pages/Account'
import { BusinessProfilePage } from '@/pages/BusinessProfile'
import { HomePage } from '@/pages/Home'
import { LoginPage } from '@/pages/Login'
import { LOGIN_PATH } from '@/config'
import { MarketsPage } from '@/pages/Markets'
import { MethodologyPage } from '@/pages/Methodology'
import { NotFoundPage } from '@/pages/NotFound'
import { ProductDetailPage } from '@/pages/ProductDetail'
import { ProductsPage } from '@/pages/Products'
import { SearchPage } from '@/pages/Search'
import { StateDetailPage, StatesPage } from '@/pages/States'

const AdminDashboardPage = lazy(() =>
  import('@/pages/admin/Dashboard').then((m) => ({ default: m.AdminDashboardPage })),
)
const AdminCrawlsPage = lazy(() =>
  import('@/pages/admin/Crawls').then((m) => ({ default: m.AdminCrawlsPage })),
)
const AdminOrganisationsPage = lazy(() =>
  import('@/pages/admin/Organisations').then((m) => ({
    default: m.AdminOrganisationsPage,
  })),
)
const AdminReviewQueuePage = lazy(() =>
  import('@/pages/admin/ReviewQueue').then((m) => ({ default: m.AdminReviewQueuePage })),
)
const AdminUsersPage = lazy(() =>
  import('@/pages/admin/Users').then((m) => ({ default: m.AdminUsersPage })),
)
const AdminSeoPage = lazy(() =>
  import('@/pages/admin/Seo').then((m) => ({ default: m.AdminSeoPage })),
)
const AdminSettingsPage = lazy(() =>
  import('@/pages/admin/Settings').then((m) => ({ default: m.AdminSettingsPage })),
)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Reference data and search results do not change between one render and
      // the next; refetching on every window focus is noise and load.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Never retry a 4xx: a 404 will still be a 404, and retrying a 401
        // three times just delays the sign-in prompt.
        if (error instanceof ApiError && !error.isTransient) return false
        return failureCount < 2
      },
    },
  },
})

function PageFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Spinner className="size-6 text-[var(--text-tertiary)]" />
    </div>
  )
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Standalone — no shell. */}
            {/* Path comes from VITE_LOGIN_PATH — see config.ts for why. */}
            <Route path={LOGIN_PATH} element={<LoginPage />} />

            {/* Public. */}
            <Route element={<PublicLayout />}>
              <Route index element={<HomePage />} />
              <Route path="search" element={<SearchPage />} />

              {/*
                The canonical URL shapes, matching `seo/url_builder.py`.

                Flask server-renders these for a first load, so a crawler gets
                complete HTML. These routes handle the SAME urls during
                client-side navigation inside the SPA, so a visitor moving
                between pages gets the richer React view without a round trip —
                one URL, two renderers, no duplicate content.
              */}
              <Route
                // Three shapes, because a business with no recorded state has
                // a shorter URL — `/businesses/nigeria/dangote-cement`. The
                // server started emitting those and this router did not know
                // them, so a link rendered "Page not found" while a refresh
                // (which reaches Flask instead) showed the page. Only the slug
                // identifies the business; the rest is description.
                path="businesses/:country/:slug"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <BusinessProfilePage />
                  </Suspense>
                }
              />
              <Route
                path="businesses/:country/:state/:slug"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <BusinessProfilePage />
                  </Suspense>
                }
              />
              <Route
                path="businesses/:country/:state/:locality/:slug"
                element={<BusinessProfilePage />}
              />
              <Route path="products" element={<ProductsPage />} />
              <Route path="products/:category/:slug" element={<ProductDetailPage />} />
              <Route path="markets" element={<MarketsPage />} />
              <Route path="states" element={<StatesPage />} />
              <Route path="states/:slug" element={<StateDetailPage />} />
              <Route path="methodology" element={<MethodologyPage />} />
              <Route path="account" element={<AccountPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* Operations. */}
            <Route
              path="/admin"
              element={
                <Suspense fallback={<PageFallback />}>
                  <AdminLayout />
                </Suspense>
              }
            >
              <Route
                index
                element={
                  <Suspense fallback={<PageFallback />}>
                    <AdminDashboardPage />
                  </Suspense>
                }
              />
              <Route
                path="organisations"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <AdminOrganisationsPage />
                  </Suspense>
                }
              />
              <Route
                path="crawls"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <AdminCrawlsPage />
                  </Suspense>
                }
              />
              <Route
                path="review"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <AdminReviewQueuePage />
                  </Suspense>
                }
              />
              <Route
                path="seo"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <AdminSeoPage />
                  </Suspense>
                }
              />
              <Route
                path="users"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <AdminUsersPage />
                  </Suspense>
                }
              />
              <Route
                path="settings"
                element={
                  <Suspense fallback={<PageFallback />}>
                    <AdminSettingsPage />
                  </Suspense>
                }
              />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
